/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * useApnsRegistration — primes iOS APNs early AND exposes a readiness gate so
 * the OTP screen can wait for the native silent-push path instead of racing it.
 *
 * WHY THE GATE EXISTS (re-introduced after the cleanup commit removed it):
 * Native Firebase Phone Auth verifies via an APNs silent-push receipt. That
 * receipt is only available once FIRAuth actually HOLDS the APNs device token
 * (Auth.auth().apnsToken != nil). Priming at mount only STARTS the token
 * arriving — it can land anywhere from ~100 ms (warm) to several seconds (cold
 * launch, slow network, or while the iOS permission dialog blocks on the user).
 *
 * If the user taps "Send code" BEFORE the token lands, Firebase has no APNs
 * receipt and silently falls back to its web reCAPTCHA flow inside the WKWebView
 * — the flaky "white reCAPTCHA page". Device testing proved this race is real
 * even with pre-auth priming. So the OTP send must WAIT for readiness:
 * waitForApns() does a LIVE check at send time (not a stale mount-time flag),
 * keyed off the authoritative native plugin (ApnsTokenPlugin → FIRAuth's real
 * token). When ready, native verifyPhoneNumber uses the silent-push path and
 * NO reCAPTCHA appears. If the user DENIED notifications, no token can ever
 * arrive: waitForApns resolves "unavailable" and the caller degrades to the web
 * fallback (no crash, no hang).
 *
 * On non-iOS platforms waitForApns resolves "ready" immediately (no APNs
 * concept) so web/Android behavior is unchanged.
 */

import { useEffect, useState } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";

export type ApnsStatus = "pending" | "ready" | "unavailable";

interface ApnsTokenPlugin {
  /** Synchronous check — is Auth.auth().apnsToken non-nil right now? */
  isReady(): Promise<{ ready: boolean }>;
  /** Poll until FIRAuth holds the APNs token or `timeoutMs` elapses. */
  awaitToken(options: { timeoutMs: number }): Promise<{ ready: boolean }>;
}

// The native guard plugin (ios/App/App/ApnsTokenPlugin.swift). On non-iOS the
// proxy is never called — platform checks short-circuit first.
const ApnsToken = registerPlugin<ApnsTokenPlugin>("ApnsToken");

const DEFAULT_WAIT_MS = 15_000;

export interface UseApnsRegistrationReturn {
  status: ApnsStatus;
  /**
   * Await a settled APNs state, bounded by `timeoutMs`. Resolves "ready" only
   * when FIRAuth actually holds the token (so the native phone-auth path is
   * safe); otherwise "unavailable" so the caller degrades to the web/reCAPTCHA
   * fallback rather than risking the white-page reCAPTCHA. Never rejects.
   */
  waitForApns: (timeoutMs?: number) => Promise<ApnsStatus>;
}

// Module singleton so every consumer shares one registration attempt + state.
// `apnsStatus` drives the hook's reactive `status`; the AUTHORITATIVE gate
// decision is made live in waitForApns (see below), not from this cached value.
let apnsStatus: ApnsStatus = "pending";
let registrationStarted = false;
// Set true only when the user DENIES notifications — then no token can ever
// arrive and waitForApns short-circuits to "unavailable" without re-polling.
let permissionDenied = false;
const listeners = new Set<(s: ApnsStatus) => void>();

function setApnsStatus(next: ApnsStatus) {
  apnsStatus = next;
  for (const l of listeners) l(next);
}

// Idempotent native APNs registration. PushNotifications.register() asks iOS to
// register with APNs; the resulting token reaches FIRAuth via
// AppDelegate.didRegister → setAPNSToken. Safe to call repeatedly — iOS coalesces
// registration, and FIRAuth holding the token is the authoritative gate.
async function registerForApns(): Promise<void> {
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    await PushNotifications.register();
  } catch (regErr) {
    console.warn("[useApnsRegistration] register() failed:", regErr);
  }
}

// Kick off permission + APNs registration ONCE, early (at app mount). This only
// STARTS the token arriving — it does NOT make the gate decision. The token can
// land seconds later (slow network), so the readiness check that gates the OTP
// send happens live in waitForApns, at send time.
async function startRegistrationOnce(): Promise<void> {
  if (registrationStarted) return;
  registrationStarted = true;

  const platform = Capacitor.getPlatform();
  if (platform !== "ios") {
    setApnsStatus("ready"); // no APNs concept on web/Android
    return;
  }

  try {
    // CHECK permission only — never REQUEST here. The notification permission
    // is owned by the onboarding primer (NotificationPrimerScreen → "Turn on"),
    // which is the single ask in the phone-first flow. Requesting at app mount
    // used to fire the iOS dialog on the Welcome screen and pre-empt the primer.
    // Once the primer grants, AppDelegate.didRegister → setAPNSToken delivers
    // the token to FIRAuth directly (we never call FirebaseMessaging.getToken()).
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    const perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive === "denied") {
      // DENY PATH: no token will ever arrive — mark denied so waitForApns
      // short-circuits to "unavailable" (web fallback). No crash, no hang.
      console.warn(
        "[useApnsRegistration] notifications denied — APNs unavailable, will use fallback",
      );
      permissionDenied = true;
      setApnsStatus("unavailable");
      return;
    }
    if (perm.receive === "granted") {
      // Already granted (e.g. the primer just granted, or a returning user).
      // Ensure native APNs registration is kicked so the token flows to FIRAuth.
      await registerForApns();
    }
    // granted → registration in flight; prompt/undecided → primer will ask.
    // Either way reflect a non-terminal "pending"; the real gate is the live
    // check in waitForApns (which re-runs this kickoff first).
    setApnsStatus("pending");
  } catch (err) {
    console.warn("[useApnsRegistration] setup failed:", err);
    // Don't latch unavailable here — a transient setup error shouldn't
    // permanently block the native path; waitForApns will re-check live.
  }
}

/**
 * Re-run the APNs kickoff after the primer grants notification permission.
 *
 * The primer (NotificationPrimerScreen) owns the single permission request. The
 * FIRST mount-time startRegistrationOnce() ran while permission was still
 * `prompt` (so it only checked and set the one-shot `registrationStarted`
 * guard). Once the user grants, we must re-evaluate: clear the guard and run
 * the kickoff again so the now-granted branch fires PushNotifications.register()
 * and the token starts flowing. Idempotent and safe off iOS.
 */
export async function primeApns(): Promise<void> {
  if (Capacitor.getPlatform() !== "ios") return;
  // Allow the kickoff to run again now that permission state may have changed.
  registrationStarted = false;
  await startRegistrationOnce();
}

/**
 * Gate the OTP send on whether FIRAuth ACTUALLY holds the APNs token RIGHT NOW.
 *
 * Called at SEND TIME (when the user taps "Send code"). It does a fresh, LIVE
 * native check (ApnsToken.awaitToken) against FIRAuth's current state — NOT a
 * cached mount-time result. That's the fix for the late-token race: APNs
 * registration can complete after an earlier poll gave up (slow network), so
 * the decision must reflect the token's state at the moment of the request.
 * Resolves "ready" only when FIRAuth holds the token (native path is safe), else
 * "unavailable" (web fallback). Never rejects.
 */
export async function waitForApns(
  timeoutMs: number = DEFAULT_WAIT_MS,
): Promise<ApnsStatus> {
  const platform = Capacitor.getPlatform();
  if (platform !== "ios") return "ready"; // no APNs concept off iOS

  // Await the permission/registration kickoff so the deny decision is settled
  // before we check it (startRegistrationOnce is idempotent — only the first
  // call does the work; later calls return immediately).
  await startRegistrationOnce();

  // The user explicitly denied notifications — no token is possible; don't
  // poll, go straight to the fallback.
  if (permissionDenied) return "unavailable";

  try {
    // If FIRAuth doesn't already hold the token, actively RE-KICK registration
    // before polling. This closes the race that produced the intermittent white
    // reCAPTCHA page: the mount-time register() may have failed or not yet
    // delivered, and startRegistrationOnce() is a one-shot guard that won't
    // retry. Re-registering here (idempotent) gives iOS another chance to
    // deliver the token within the poll window, so the native silent-push path
    // wins and the web reCAPTCHA fallback never appears.
    const already = await ApnsToken.isReady().catch(() => ({ ready: false }));
    if (!already.ready) {
      await registerForApns();
    }

    // LIVE check: does FIRAuth hold the token now, or within the window? This
    // re-polls every call, so a token that arrived after an earlier timeout is
    // picked up here.
    const { ready } = await ApnsToken.awaitToken({ timeoutMs });
    const status: ApnsStatus = ready ? "ready" : "unavailable";
    setApnsStatus(status);
    return status;
  } catch (err) {
    console.warn("[useApnsRegistration] waitForApns check failed:", err);
    return "unavailable";
  }
}

export function useApnsRegistration(): UseApnsRegistrationReturn {
  const [status, setStatus] = useState<ApnsStatus>(apnsStatus);

  useEffect(() => {
    listeners.add(setStatus);
    setStatus(apnsStatus);
    void startRegistrationOnce();
    return () => {
      listeners.delete(setStatus);
    };
  }, []);

  return { status, waitForApns };
}
